import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { CategoryRowIcon } from './categoryIcons';

const FALLBACK = [
  { id: 'fb-bm', name: 'Building Materials', subcategories: [{ id: 's1', name: 'Cement', slug: 'cement' }] },
  { id: 'fb-el', name: 'Electronics', subcategories: [{ id: 's2', name: 'Components', slug: 'components' }] },
  { id: 'fb-mc', name: 'Machinery', subcategories: [{ id: 's3', name: 'Industrial', slug: 'industrial' }] },
  { id: 'fb-ap', name: 'Apparel', subcategories: [{ id: 's4', name: 'Workwear', slug: 'workwear' }] },
  { id: 'fb-sp', name: 'Sports', subcategories: [{ id: 's5', name: 'Equipment', slug: 'equipment' }] },
  { id: 'fb-be', name: 'Beauty', subcategories: [{ id: 's6', name: 'Personal care', slug: 'personal-care' }] },
  { id: 'fb-hg', name: 'Home & Garden', subcategories: [{ id: 's7', name: 'Furniture', slug: 'furniture' }] },
  { id: 'fb-ie', name: 'Industrial Equipment', subcategories: [{ id: 's8', name: 'Tools', slug: 'tools' }] },
  { id: 'fb-ce', name: 'Consumer Electronics', subcategories: [{ id: 's9', name: 'Gadgets', slug: 'gadgets' }] },
  { id: 'fb-je', name: 'Jewelry & Watches', subcategories: [{ id: 's10', name: 'Watches', slug: 'watches' }] },
];

export default function MarketplaceCategorySidebar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [hovered, setHovered] = useState(null);
  const selectedCategoryId = searchParams.get('category');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (response.ok) {
          const data = await response.json();
          const arr = Array.isArray(data) ? data : data.categories || data.value || [];
          setCategories(arr.length ? arr : FALLBACK);
        } else setCategories(FALLBACK);
      } catch {
        setCategories(FALLBACK);
      }
    };
    fetchCategories();
  }, []);

  const list = categories.length ? categories : FALLBACK;

  const goCategory = (cat) => {
    if (typeof cat.id === 'string' && cat.id.startsWith('fb-')) {
      navigate(`/products?search=${encodeURIComponent(cat.name)}`);
      return;
    }
    navigate(`/products?category=${cat.id}`);
  };

  const goSub = (cat, sub) => {
    if (typeof cat.id === 'string' && cat.id.startsWith('fb-')) {
      navigate(`/products?search=${encodeURIComponent(sub.name || sub)}`);
      return;
    }
    navigate(`/products?category=${cat.id}&subcategory=${encodeURIComponent(sub.slug || sub.name || '')}`);
  };

  return (
    <aside
      className="mh-cat-sidebar mh-bento-sidebar-col"
      style={{
        borderRadius: 12,
        background: '#fff',
        border: '1px solid #ebebeb',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'visible',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      }}
      onMouseLeave={() => setHovered(null)}
    >
      {/* Header */}
      <div
        className="mh-cat-head"
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <svg
          style={{ width: 18, height: 18, flexShrink: 0, color: '#ff6a00' }}
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
        </svg>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1b1b1b', margin: 0 }}>Categories for you</h2>
      </div>

      {/* Category list */}
      <ul
        className="mh-cat-list"
        style={{ listStyle: 'none', margin: 0, padding: '6px 0', flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
      >
        {list.slice(0, 12).map((cat) => {
          const subs = cat.subcategories || [];
          const open = expandedId === cat.id;
          const active = selectedCategoryId === String(cat.id);

          return (
            <li key={cat.id} style={{ position: 'relative' }}>
              <button
                type="button"
                onMouseEnter={() => setHovered(cat)}
                onClick={() => {
                  if (subs.length) setExpandedId(open ? null : cat.id);
                  else goCategory(cat);
                }}
                className="mh-cat-item-btn"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 14px',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? '#ff6a00' : '#1b1b1b',
                  background: active ? 'rgba(255,106,0,0.06)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background .15s ease, color .15s ease',
                }}
                onMouseOver={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'rgba(255,106,0,0.04)';
                    e.currentTarget.style.color = '#ff6a00';
                  }
                }}
                onMouseOut={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1b1b1b';
                  }
                }}
              >
                <CategoryRowIcon name={cat.name} />
                <span style={{ flex: 1 }}>{cat.name}</span>
                {subs.length > 0 && (
                  <svg
                    style={{
                      width: 14,
                      height: 14,
                      color: '#aaa',
                      flexShrink: 0,
                      transition: 'transform .2s ease',
                      transform: open ? 'rotate(90deg)' : 'none',
                    }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>

              {/* Sub-categories accordion */}
              <div
                style={{
                  overflow: 'hidden',
                  maxHeight: open && subs.length ? '240px' : 0,
                  opacity: open && subs.length ? 1 : 0,
                  transition: 'max-height .3s ease, opacity .3s ease',
                }}
              >
                <ul style={{ listStyle: 'none', margin: 0, padding: '4px 14px 8px 40px' }}>
                  {subs.slice(0, 8).map((sub) => (
                    <li key={sub.id || sub.name}>
                      <button
                        type="button"
                        onClick={() => goSub(cat, sub)}
                        style={{
                          fontSize: 12,
                          color: '#666',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          transition: 'color .15s ease',
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.color = '#ff6a00'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = '#666'; }}
                      >
                        {sub.name || sub}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          );
        })}
      </ul>

      {/* "All categories" footer button */}
      <button
        type="button"
        onClick={() => navigate('/all-categories')}
        style={{
          width: '100%',
          padding: '10px 0',
          fontSize: 13,
          fontWeight: 600,
          color: '#ff6a00',
          background: 'none',
          border: 'none',
          borderTop: '1px solid #f3f4f6',
          cursor: 'pointer',
          borderRadius: '0 0 12px 12px',
          transition: 'background .15s ease',
          flexShrink: 0,
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,106,0,0.04)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'none'; }}
      >
        All categories →
      </button>

      {/* Hover flyout for sub-categories */}
      {hovered && hovered.subcategories?.length > 0 && (
        <div
          style={{
            display: 'none',
          }}
          className="hidden xl:block"
          onMouseEnter={() => setHovered(hovered)}
        >
          {/* intentionally hidden – accordion already shows subs on click */}
        </div>
      )}
    </aside>
  );
}
